import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import { SectionTitle, StatusPill, EmptyState, Avatar, ShowMore } from "../../components/Atoms";
import { CheckCircle, XCircle, Megaphone, Link as LinkIcon, FileArrowDown, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const TABS = ["Pending", "Approved", "Rejected"];

export default function SubmissionsPage() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("Pending");
  const [feedback, setFeedback] = useState({});
  const [visible, setVisible] = useState(10);

  const load = useCallback(async () => {
    const { data } = await api.get(`/submissions?status=${tab}`);
    setItems(data);
    setVisible(10);
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    await api.post(`/submissions/${id}/${action}`, { feedback: feedback[id] || "" });
    toast.success(`Submission ${action}d`); load();
  };

  return (
    <div className="space-y-5">
      <SectionTitle title="Submissions" subtitle="Review creator task proofs. Tip: open a project to see submissions grouped per task." />
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`subs-tab-${t.toLowerCase()}`}
            className={`px-4 py-2 rounded-full text-sm font-bold ${tab === t ? "bg-white/10 text-white border border-white/15" : "text-muted bg-white/5"}`}>{t}</button>
        ))}
      </div>
      {items.length === 0 ? <EmptyState icon={Megaphone} title={`No ${tab.toLowerCase()} submissions`} /> : (
        <>
        <div className="grid lg:grid-cols-2 gap-4">
          {items.slice(0, visible).map(s => (
            <div key={s.id} className="glass p-4 hover-lift" data-testid={`submission-${s.id}`}>
              <div className="flex items-center gap-3">
                <Link to={`/admin/ambassadors/${s.ambassador_id}`} className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80" data-testid={`sub-amb-link-${s.id}`}>
                  <Avatar src={s.ambassador_photo} name={s.ambassador_name} />
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-bold truncate hover:text-[#22D3EE]">{s.task_title}</div>
                    <div className="text-xs text-muted truncate">{s.ambassador_name} · {s.project_title}</div>
                  </div>
                </Link>
                <StatusPill status={s.status} />
              </div>
              {s.notes && <div className="text-sm text-muted mt-3">{s.notes}</div>}
              <div className="flex flex-wrap gap-2 mt-3">
                {s.link && <a href={s.link} target="_blank" rel="noreferrer" className="btn-ghost text-xs flex items-center gap-1.5"><LinkIcon size={14} /> Open link</a>}
                {s.file_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${s.file_url}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs flex items-center gap-1.5"><FileArrowDown size={14} /> File</a>}
                <Link to={`/admin/projects/${s.project_id}`} className="btn-ghost text-xs flex items-center gap-1.5 ml-auto text-[#22D3EE]" data-testid={`sub-go-project-${s.id}`}>Open project <ArrowRight size={12} weight="bold" /></Link>
              </div>
              {s.feedback && <div className="text-xs text-[#F59E0B] mt-2">Feedback: {s.feedback}</div>}
              {s.status === "Pending" && (
                <div className="mt-3 space-y-2">
                  <input placeholder="Feedback (optional)" value={feedback[s.id] || ""} onChange={e => setFeedback({...feedback, [s.id]: e.target.value})} className="input-base text-sm" data-testid={`feedback-${s.id}`} />
                  <div className="flex gap-2">
                    <button onClick={() => act(s.id, "approve")} className="btn-ghost flex-1 text-[#A3E635] flex items-center justify-center gap-1.5 text-sm" data-testid={`approve-sub-${s.id}`}><CheckCircle size={16} weight="bold" /> Approve</button>
                    <button onClick={() => act(s.id, "reject")} className="btn-ghost flex-1 text-[#EC4899] flex items-center justify-center gap-1.5 text-sm" data-testid={`reject-sub-${s.id}`}><XCircle size={16} weight="bold" /> Reject</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <ShowMore visible={visible} total={items.length} onMore={setVisible} testid="subs-show-more" />
        </>
      )}
    </div>
  );
}
